import { useEffect, useRef, type ReactNode } from 'react';
import type { TimerState } from '@sincronia/shared';
import { formatDuration, formatInterval } from '../../lib/format.js';
import { useGameStore } from '../../store/gameStore.js';
import { useT } from '../../i18n/index.js';
import { cn } from '../../lib/ui.js';

type Anchored = TimerState & { serverNow: number };

function intervalMs(timer: Anchored, now: number): number {
  return timer.running ? Math.max(0, now - timer.intervalAnchor) : timer.intervalFrozenMs;
}

function levelMs(timer: Anchored, now: number): number {
  return timer.running
    ? timer.levelAccumulatedMs + Math.max(0, now - timer.levelAnchor)
    : timer.levelAccumulatedMs;
}

/**
 * Cronômetro sincronizado.
 *
 * O servidor envia apenas âncoras; o cliente renderiza com
 * requestAnimationFrame usando o relógio corrigido pelo offset medido, e
 * escreve direto no DOM para não re-renderizar dez vezes por segundo.
 */
export function GameTimer({ timer, paused }: { timer: Anchored; paused: boolean }): ReactNode {
  const t = useT();
  const intervalRef = useRef<HTMLSpanElement>(null);
  const totalRef = useRef<HTMLSpanElement>(null);
  const timerRef = useRef(timer);
  timerRef.current = timer;

  useEffect(() => {
    let frame = 0;
    let lastInterval = '';
    let lastTotal = '';

    const tick = (): void => {
      const offset = useGameStore.getState().clockOffset;
      const now = Date.now() + offset;
      const current = timerRef.current;

      const nextInterval = formatInterval(intervalMs(current, now));
      if (nextInterval !== lastInterval && intervalRef.current) {
        lastInterval = nextInterval;
        intervalRef.current.textContent = nextInterval;
      }

      const nextTotal = formatDuration(levelMs(current, now));
      if (nextTotal !== lastTotal && totalRef.current) {
        lastTotal = nextTotal;
        totalRef.current.textContent = nextTotal;
      }

      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <div className="flex flex-col items-center gap-1" data-testid="timer">
      <span className="sr-only">{t('timer.interval')}</span>
      <span
        ref={intervalRef}
        data-testid="timer-interval"
        aria-live="off"
        className={cn(
          'tabular text-4xl leading-none font-semibold tracking-tight sm:text-5xl',
          paused ? 'text-night-400' : 'text-night-200',
        )}
      >
        00:00.0
      </span>
      <span className="flex items-center gap-1.5 text-xs text-night-400">
        {t('timer.total')}
        <span ref={totalRef} data-testid="timer-total" className="tabular font-medium">
          00:00
        </span>
      </span>
    </div>
  );
}
